/**
 * Deals: a board of stages or a table, over the same filters.
 *
 * The filters live in the address (pipeline, view, search, owner, close
 * dates), so a board can be bookmarked or sent to someone, and switching
 * between board and table keeps them. `?deal=<id>` opens a deal's drawer and
 * `?new=1` the new deal form, which is how ⌘K reaches both.
 *
 * Column totals come from the summary endpoint with the same filters, so a
 * column's figures always match the cards under it, however many there are.
 */
import { useState } from 'react';
import {
  Alert, Badge, Button, Card, Checkbox, Group, Loader, Pagination, Select, Stack, Table, Text, TextInput,
} from '@mantine/core';
import { IconAdjustments, IconLayoutKanban, IconList, IconPlus, IconSearch } from '@tabler/icons-react';
import { useSearchParams } from 'react-router-dom';
import { useDeals, useDealsSummary, usePipelines, type DealListParams } from '../../api/crm';
import { FONTS, TOKENS } from '../../theme';
import { MonoLabel, MonoSegmented, PageHeader } from '../../ui';
import { navLabel, useVocab } from '../../vocab';
import { DealBoard } from './DealBoard';
import { DealDrawer } from './DealDrawer';
import { NewDealModal, useStageMove } from './DealForms';
import { PipelinesModal } from './PipelinesModal';
import { BulkAssignBar } from '../assignment/OwnerControls';
import { ColumnPicker, PropertyCells, PropertyHeaders, usePropertyColumns } from '../properties/PropertyColumns';
import {
  formatDay, formatPence, statusColour, STATUS_LABEL, useCrmPermissions, useOwnerOptions,
} from './crmShared';

const PAGE_SIZE = 50;
/** The board shows every card in one go; past this the table is the better view. */
const BOARD_LIMIT = 500;

export function DealsPage() {
  const v = useVocab();
  const perms = useCrmPermissions();
  const cols = usePropertyColumns('deal', perms.recordProperties);
  const columns = perms.recordProperties ? cols.columns : [];
  const [params, setParams] = useSearchParams();
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, val] of Object.entries(patch)) {
      if (val === null || val === '') next.delete(k);
      else next.set(k, val);
    }
    setParams(next, { replace: true });
  };

  const pipelinesQuery = usePipelines();
  const active = pipelinesQuery.data?.pipelines.filter((p) => !p.archived) ?? [];
  const pipelineId = params.get('pipeline') ?? active[0]?.id ?? null;
  const pipeline = pipelinesQuery.data?.pipelines.find((p) => p.id === pipelineId) ?? active[0];
  const view = params.get('view') === 'table' ? 'table' : 'board';
  const q = params.get('q') ?? '';
  const owner = params.get('owner') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const status = params.get('status') ?? '';
  const [page, setPage] = useState(1);
  const [pipelinesOpen, setPipelinesOpen] = useState(false);
  const owners = useOwnerOptions();
  const mover = useStageMove();

  const shared = {
    pipeline_id: pipeline?.id,
    q,
    owner_user_id: owner,
    close_from: from,
    close_to: to,
  };
  const listParams: DealListParams = view === 'board'
    ? { ...shared, page_size: BOARD_LIMIT }
    : { ...shared, status, page, page_size: PAGE_SIZE };
  const deals = useDeals(listParams, !!pipeline);
  const rows = deals.data?.deals ?? [];
  const [picked, setPicked] = useState<string[]>([]);
  const summary = useDealsSummary(shared, !!pipeline);

  const openDeal = params.get('deal');
  const creating = params.get('new') === '1';
  const s = summary.data;

  return (
    <Stack gap="md">
      <PageHeader
        title={navLabel(v, 'deals')}
        actions={(
          <>
            {perms.managePipelines && (
              <Button variant="default" leftSection={<IconAdjustments size={16} />} onClick={() => setPipelinesOpen(true)}>
                {v.Many('pipeline')}
              </Button>
            )}
            {perms.editDeals && (
              <Button leftSection={<IconPlus size={16} />} onClick={() => set({ new: '1' })}>
                {`New ${v.one('deal')}`}
              </Button>
            )}
          </>
        )}
      />

      <Group gap="sm" align="flex-end" wrap="wrap">
        {active.length > 1 && (
          <Select
            label={v.One('pipeline')}
            data={active.map((p) => ({ value: p.id, label: p.name }))}
            value={pipeline?.id ?? null}
            onChange={(id) => { setPage(1); set({ pipeline: id }); }}
            allowDeselect={false}
            w={200}
          />
        )}
        <TextInput
          label="Search"
          placeholder={`${v.One('deal')} or ${v.one('company')}`}
          leftSection={<IconSearch size={14} />}
          value={q}
          onChange={(e) => { setPage(1); set({ q: e.currentTarget.value }); }}
          w={220}
        />
        <Select
          label="Owner"
          placeholder="Anyone"
          clearable
          searchable
          data={[{ value: 'me', label: 'Mine' }, { value: 'none', label: 'Nobody' }, ...owners]}
          value={owner || null}
          onChange={(o) => { setPage(1); set({ owner: o }); }}
          w={180}
        />
        <TextInput
          label="Closing from"
          type="date"
          value={from}
          onChange={(e) => { setPage(1); set({ from: e.currentTarget.value }); }}
          w={160}
        />
        <TextInput
          label="to"
          type="date"
          value={to}
          onChange={(e) => { setPage(1); set({ to: e.currentTarget.value }); }}
          w={160}
        />
        {view === 'table' && (
          <Select
            label="Status"
            placeholder="Any"
            clearable
            data={[{ value: 'open', label: 'Open' }, { value: 'won', label: 'Won' }, { value: 'lost', label: 'Lost' }]}
            value={status || null}
            onChange={(st) => { setPage(1); set({ status: st }); }}
            w={130}
          />
        )}
        <Group gap="sm" style={{ marginLeft: 'auto' }} align="center">
          {view === 'table' && perms.recordProperties && (
            <ColumnPicker defs={cols.defs} chosen={cols.chosen} onChange={cols.setChosen} />
          )}
          <MonoSegmented
            value={view}
            onChange={(val) => set({ view: val === 'table' ? 'table' : null })}
            data={[
              { value: 'board', label: <Group gap={4} wrap="nowrap"><IconLayoutKanban size={14} />Board</Group> },
              { value: 'table', label: <Group gap={4} wrap="nowrap"><IconList size={14} />Table</Group> },
            ]}
          />
        </Group>
      </Group>

      {s && (
        <Group gap="xl" wrap="wrap">
          <Figure label="Open" value={`${s.open_count}`} />
          <Figure label="Open value" value={formatPence(s.open_amount_pence)} />
          <Figure label="Weighted" value={formatPence(s.weighted_pence)} />
          <Figure label="Won" value={formatPence(s.won_amount_pence)} />
        </Group>
      )}

      {(pipelinesQuery.error || deals.error) && (
        <Alert color="red" variant="light">{(pipelinesQuery.error ?? deals.error)?.message}</Alert>
      )}

      {!pipeline || deals.isPending ? (
        <Group justify="center" p="xl"><Loader /></Group>
      ) : view === 'board' ? (
        <>
          {(deals.data?.pagination.total ?? 0) > BOARD_LIMIT && (
            <Alert variant="light" color="orange">
              {`The board shows the newest ${BOARD_LIMIT} ${v.many('deal')}. Use the filters, or the table, to reach the rest.`}
            </Alert>
          )}
          <DealBoard
            pipeline={pipeline}
            deals={deals.data?.deals ?? []}
            totals={s?.stages}
            canEdit={perms.editDeals}
            movingId={mover.moving}
            onOpen={(id) => set({ deal: id })}
            onMove={(deal, stage) => mover.request(deal, stage)}
          />
        </>
      ) : (
        <Card p={0}>
          <BulkAssignBar kind="deals" ids={picked} onClear={() => setPicked([])} onDone={() => setPicked([])} />
          <Table.ScrollContainer minWidth={760}>
            <Table verticalSpacing={8} highlightOnHover data-testid="deals-table">
              <Table.Thead>
                <Table.Tr>
                  {perms.assignRecords && (
                    <Table.Th w={36}>
                      <Checkbox
                        aria-label="Select every row on this page"
                        checked={rows.length > 0 && rows.every((d) => picked.includes(d.id))}
                        indeterminate={picked.length > 0 && !rows.every((d) => picked.includes(d.id))}
                        onChange={(e) => setPicked(e.currentTarget.checked ? rows.map((d) => d.id) : [])}
                      />
                    </Table.Th>
                  )}
                  <Table.Th>Name</Table.Th>
                  <Table.Th>{v.One('company')}</Table.Th>
                  <Table.Th>{v.One('stage')}</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th ta="right">Amount</Table.Th>
                  <Table.Th>Close date</Table.Th>
                  <Table.Th>Owner</Table.Th>
                  <PropertyHeaders columns={columns} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {deals.data?.deals.length === 0 && (
                  <Table.Tr><Table.Td colSpan={8 + columns.length}>
                    <Text ta="center" c="dimmed" py="lg">{`No ${v.many('deal')} match.`}</Text>
                  </Table.Td></Table.Tr>
                )}
                {deals.data?.deals.map((d) => (
                  <Table.Tr key={d.id} onClick={() => set({ deal: d.id })} style={{ cursor: 'pointer' }}>
                    {perms.assignRecords && (
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          aria-label={`Select ${d.name}`}
                          checked={picked.includes(d.id)}
                          onChange={(e) => {
                            const on = e.currentTarget.checked;
                            setPicked((all) => (on ? [...all, d.id] : all.filter((x) => x !== d.id)));
                          }}
                        />
                      </Table.Td>
                    )}
                    <Table.Td><Text size="sm" fw={500}>{d.name}</Text></Table.Td>
                    <Table.Td><Text size="sm">{d.company_name ?? ''}</Text></Table.Td>
                    <Table.Td><Text size="sm">{d.stage_name}</Text></Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="light" color={statusColour(d.status)}>{STATUS_LABEL[d.status]}</Badge>
                    </Table.Td>
                    <Table.Td ta="right">
                      <span style={{ fontFamily: FONTS.mono, fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
                        {formatPence(d.amount_pence)}
                      </span>
                    </Table.Td>
                    <Table.Td><Text size="sm">{formatDay(d.close_date)}</Text></Table.Td>
                    <Table.Td>
                      {d.owner_name ? <Text size="sm">{d.owner_name}</Text> : <Text size="sm" c="dimmed">Nobody</Text>}
                    </Table.Td>
                    <PropertyCells columns={columns} values={d.properties} />
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {(deals.data?.pagination.total_pages ?? 0) > 1 && (
            <Group justify="space-between" p="sm" style={{ borderTop: `1px solid ${TOKENS.border}` }}>
              <Text size="xs" c="dimmed">{v.count(deals.data?.pagination.total ?? 0, 'deal')}</Text>
              <Pagination size="sm" total={deals.data?.pagination.total_pages ?? 1} value={page} onChange={setPage} />
            </Group>
          )}
        </Card>
      )}

      <DealDrawer id={openDeal} onClose={() => set({ deal: null })} />
      <NewDealModal
        opened={creating}
        onClose={() => set({ new: null })}
        prefill={{ pipelineId: pipeline?.id ?? null }}
        onCreated={(id) => set({ new: null, deal: id })}
      />
      <PipelinesModal opened={pipelinesOpen} onClose={() => setPipelinesOpen(false)} initialId={pipeline?.id} />
      {mover.dialog}
    </Stack>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <MonoLabel>{label}</MonoLabel>
      <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: TOKENS.text, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
    </div>
  );
}
